import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import './DiagnosticStyles.css';
import {COL_DEFS, defaultPDFParams, FB_MESSAGES, GRID_ENUMS, GridStyle} from "./Styles";
import {AgGridReact} from "ag-grid-react";
import {FailCellRenderer, PassCellRenderer, addStudentColDefs, showHideUserCols, sortColumns} from "./Utils";
import TestQuestionResultDialog from "./TestQuestionResultDialog";
import React, {useCallback, useEffect, useRef, useState} from "react";
import {printDoc} from "./pdfExport/printDoc";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const ClassQuestionViewDiagnostic = props => {
    const {
            isDiagnostic,
            testAssignmentsData,
            studentTestAssignmentsMap,
            questionCount,
            isLoading,
            setLoading,
            students,
            studentIds,
            studentColOrder,
            selectedDifficultyIds,
            selectedTestIds,
            headerHeightOffset,
            openDialogCallback,
            triggerGenerate
        } = props,
        REPORT_NAME = 'ClassQuestionViewReport',
        rowHeight = 27,
        defaultColDef = COL_DEFS.defaultColDef,
        correctColDef = COL_DEFS.correctColDef,
        questionColDef = COL_DEFS.questionColDef,
        difficultyColDef = COL_DEFS.difficultyColDef,
        answerColDef = COL_DEFS.answerColDef;

    const initCols = [questionColDef, difficultyColDef, correctColDef, answerColDef];
    const headerRef = useRef(null);
    const gridRef = useRef(),
        [dataMap, setDataMap] = useState({}),
        [testQuestionDialogData, setTestQuestionDialogData] = useState(null),
        [columnDefs, setColumnDefs] = useState([]),
        [graphData, setGraphData] = useState(null),
        [unfilteredData, setUnfilteredData] = useState([]);

    useEffect(() => {
        if (students.length > 0) {
            addStudentColDefs(students, initCols, false, openDialogCallback);
        }
        setColumnDefs(initCols);
    }, [students]);

    useEffect(() => {
        showHideUserCols(gridRef, columnDefs, studentIds);
        if (gridRef.current !== undefined) {
            let dataMapResults = {};
            testAssignmentsData.forEach((testAssignmentData) => {
                mapTestData(testAssignmentData, dataMapResults);
            });
            setDataMap(dataMapResults);
        }
    }, [studentIds]);

    const onGridReady = ()=>{
        showHideUserCols(gridRef,columnDefs,studentIds);
        setLoading(false);
    }

    const doSortColumns = () => {
        let columns = [...columnDefs];
        if (columns.length === 0) {
            return;
        }
        let sortedColumns = sortColumns(columns, studentColOrder);
        setColumnDefs(sortedColumns);
    }

    useEffect(() => {
        doSortColumns();
    }, [studentColOrder, graphData]);

    const onExportCSV = useCallback(() => {
        gridRef.current.api.exportDataAsCsv({
            fileName: REPORT_NAME,
            processCellCallback: cell => {
                if (cell.column.getUserProvidedColDef().columnType === GRID_ENUMS.USER) {
                    //commented out to change to 1s and 0s
                    // if(String(cell.value).indexOf('%') === -1){
                    //     let val;
                    //     if(cell.value >= 1){
                    // val = '✓';
                    // val = '1';
                    // }else if(cell.value === 0){
                    // val = '✗';
                    // val = '0';
                    // }
                        // cell.value = val;
                    // }
                }else if(cell.column.colId === GRID_ENUMS.CORRECT_PERCENT){
                    if(cell.value === GRID_ENUMS.NOT_AVAILABLE){
                        cell.value = '';
                    }
                }
                return cell.value;
            }
        });
    }, []);

    const onExportPDF = useCallback(() => {
        printDoc(defaultPDFParams,gridRef.current.api,REPORT_NAME);
    }, []);

    const mapQuestions = (testAssignmentResult, rowData) =>{
        const questionsData = testAssignmentResult.testQuestionBeans;
        questionsData.forEach(question => {
            let questionTitle;
            if(isDiagnostic){
                if(testAssignmentResult.adaptive){
                    questionTitle = (testAssignmentResult.testGroupCodeDescription + ' ' + (question.questionSeq+1));
                }else{
                    questionTitle = (testAssignmentResult.testGroupCodeDescription + ' ' + question.questionNumber);
                }
            }else{
                questionTitle = 'Question ' + (question.questionSeq + 1);
            }
            let row = {
                testId: testAssignmentResult.testId,
                testAssignmentId: testAssignmentResult.testAssignmentId,
                questionId: question.questionId,
                difficulty: question.testDiffLevel,
                questionTitle: questionTitle,
                questionNumber: question.questionNumber,
                questionMaxMark: 0,
                questionAnsweredMaxMark: 0,
                questionCorrectAmount: 0,
                questionAnsweredAmount: 0,
                shownUpAmount: 0,
                testGroupCode: testAssignmentResult.testGroupCode
            };
            students.forEach(() => {
                row[GRID_ENUMS.CORRECT_PERCENT] = GRID_ENUMS.NOT_AVAILABLE;
                row[GRID_ENUMS.CORRECT_PERCENT_VALUE] = -1;
                row.total = false;
                row[GRID_ENUMS.ALL_ANSWERED_PERCENT] = '';
                row[GRID_ENUMS.ALL_ANSWERED_PERCENT_VALUE] = -1;
            });
            rowData[question.questionId + '' + testAssignmentResult.testId] = row;
        });
    }

    const organiseData = dataArr => {
        let rowData = [];
        let columnPerformance = {};
        while (rowData.length < questionCount * dataArr.length) {
            for (let i = 0; i < dataArr.length; i++) {
                rowData.push(dataArr[i].shift());
            }
        }

        for (let i = 0; i < selectedTestIds.length; i++) {
            const testId = selectedTestIds[i];
            if (dataMap[testId]) {
                for (let j = 0; j < dataMap[testId].lastRow.length; j++) {
                    let lastRow = dataMap[testId].lastRow[j];
                    students.map(student => {
                        if (!lastRow.hasOwnProperty(GRID_ENUMS.USER + student.userId)) {
                            lastRow[GRID_ENUMS.USER + student.userId] = GRID_ENUMS.NOT_AVAILABLE;
                            columnPerformance[student.userId] = {performance: 0, answered: false};
                        } else {
                            let value = parseFloat(lastRow[GRID_ENUMS.USER + student.userId].replace('%', ''));
                            if (columnPerformance.hasOwnProperty(student.userId) && !isNaN(value)) {
                                columnPerformance[student.userId].performance = (columnPerformance[student.userId].performance + value) / selectedTestIds.length;
                                columnPerformance[student.userId].answered = true;
                            } else {
                                if (!isNaN(value)) {
                                    columnPerformance[student.userId] = {performance: value, answered: true};
                                } else {
                                    columnPerformance[student.userId] = {performance: 0, answered: false};
                                }
                            }
                        }
                    });
                    rowData.push(lastRow);
                }
            }
        }

        columnDefs.forEach(column =>{
            if(column.columnType === GRID_ENUMS.USER){
                if (columnPerformance.hasOwnProperty(column.userId) && columnPerformance[column.userId].hasOwnProperty('performance')) {
                    column.performance = columnPerformance[column.userId].performance;
                    column.answered = columnPerformance[column.userId].answered;
                }
            }
        });

        return rowData;
    }

    useEffect(() => {
        if (selectedTestIds.length === 0 || Object.keys(dataMap).length === 0) {
            return;
        }
        let dataArr = mapData(dataMap);
        let rowData = organiseData(dataArr);
        let selectedData = filterData(rowData);
        setUnfilteredData(rowData);
        setGraphData(selectedData);
    }, [dataMap]);

    const mapData = (data) => {
        let arr = [];
        for (let i = 0; i < selectedTestIds.length; i++) {
            if (data[selectedTestIds[i]] !== undefined) {
                arr.push([...data[selectedTestIds[i]].mainRows]);
            }
        }
        return arr;
    }

    const mapTestData = (testAssignmentData, dataMapResults) => {
        let rowData = {};
        let lastRowData = {};

        lastRowData[testAssignmentData.testId] = {
            testId: testAssignmentData.testId,
            testGroupCode: testAssignmentData.testGroupCode,
            questionTitle: (isDiagnostic ? testAssignmentData.testGroupCodeDescription + ' ' : '') + 'TOTAL',
            difficulty: '',
            correctPercent: GRID_ENUMS.NOT_AVAILABLE,
            correctPercentValue: 0,
            allAnsweredPercent: GRID_ENUMS.NOT_AVAILABLE,
            allAnsweredPercentValue: 0,
            questionAnsweredAmount: 0,
            studentsAnsweredCount: 0,
            studentsMarksTotal: 0
        };
        mapQuestions(testAssignmentData, rowData);
        testAssignmentData.testResultBeans.forEach(studentTestResult => {
            if (studentIds.includes(studentTestResult.userId)) {
                mapTestResults(studentTestResult, rowData, lastRowData);
            }
        })
        dataMapResults[testAssignmentData.testId] = {
            lastRow: [...Object.values(lastRowData)],
            mainRows: [...Object.values(rowData)]
        };
    }

    const mapTestResults = (studentResultData, rowData, lastRowData) => {
        const lastRow = lastRowData[studentResultData.testId];
        studentResultData.testQuestionResults.forEach((testQuestionResult) => {
            const row = rowData[testQuestionResult.questionId + '' + studentResultData.testId];
            row[GRID_ENUMS.USER + studentResultData.userId] = testQuestionResult.mark !== undefined ? testQuestionResult.mark : '';
            row.questionCorrectAmount += testQuestionResult.mark ? testQuestionResult.mark : 0;
            row.questionMaxMark += testQuestionResult.adaptiveMaxMark !== 0 ? testQuestionResult.adaptiveMaxMark : testQuestionResult.maxMark;
            const correctPercentValue = (row.questionMaxMark !== 0 || row.questionCorrectAmount !== 0) ? Math.round(100 * row.questionCorrectAmount / row.questionMaxMark) : 0;
            row[GRID_ENUMS.CORRECT_PERCENT_VALUE] = correctPercentValue;
            row[GRID_ENUMS.CORRECT_PERCENT] = correctPercentValue + '%';
            row.questionAnsweredAmount++;
            lastRow.questionAnsweredAmount++;
            const answeredPercentValue = studentIds.length > 0 ? Math.round(100 * row.questionAnsweredAmount / studentIds.length) : 0;
            row[GRID_ENUMS.ALL_ANSWERED_PERCENT_VALUE] = answeredPercentValue;
            row[GRID_ENUMS.ALL_ANSWERED_PERCENT] = answeredPercentValue + '%';
        });
        const studentCorrectPercentValue = (studentResultData.mark !== undefined && studentResultData.maxMark !== undefined && studentResultData.maxMark !== 0) ? Math.round(100 * studentResultData.mark / studentResultData.maxMark) : null;
        lastRow.total = true;

        if (studentCorrectPercentValue !== null) {
            lastRow.studentsAnsweredCount++;
            lastRow.correctPercentValue += studentCorrectPercentValue;
            lastRow[GRID_ENUMS.USER + studentResultData.userId] = studentCorrectPercentValue + '%';
            lastRow[GRID_ENUMS.CORRECT_PERCENT] = Math.round(lastRow.correctPercentValue / lastRow.studentsAnsweredCount) + '%';
        } else {
            lastRow[GRID_ENUMS.USER + studentResultData.userId] = '';
        }

        lastRow[GRID_ENUMS.ALL_ANSWERED_PERCENT] = Math.round(100 * lastRow.studentsAnsweredCount / studentIds.length) + '%';
    }

    const filterData = (data) => {
        let selectedData;
        if (selectedDifficultyIds.length > 0) {
            selectedData = data.filter(row => selectedDifficultyIds.includes(row.difficulty));
        } else {
            selectedData = data;
        }

        return selectedData;
    }

    useEffect(() => {
        let selectedData = filterData(unfilteredData);
        setGraphData(selectedData);
    }, [selectedDifficultyIds]);

    useEffect(() => {
        if (selectedTestIds === undefined || selectedTestIds.length === 0 || testAssignmentsData.length === 0) {
            return;
        }
        setGraphData(null);

        let dataMapResults = {};
        testAssignmentsData.forEach((testAssignmentData) => {
            mapTestData(testAssignmentData, dataMapResults);
        });

        if(Object.entries(dataMapResults).length > 0){
            setDataMap(dataMapResults);
        }else{
            setLoading(false);
        }
    },[triggerGenerate]);

    const handleCellClick = (cell) => {
        if (cell.data.questionId !== undefined) {
            let testAssignmentId = cell.data.testAssignmentId;

            if (testAssignmentId === undefined) {
                if (studentTestAssignmentsMap[cell.colDef.userId]) {
                    testAssignmentId = studentTestAssignmentsMap[cell.colDef.userId][cell.data.testId].get(0);
                }
            }

            if (cell.colDef.field.includes(GRID_ENUMS.USER) && cell.value !== undefined && testAssignmentId !== undefined) {
                const title = cell.data.questionTitle;
                setTestQuestionDialogData({
                    testAssignmentId: testAssignmentId,
                    questionId: cell.data.questionId,
                    userId: cell.colDef.userId,
                    title: title,
                    studentName: cell.colDef.studentName
                })
            }
        }
    }

    const postSortRows = params=> {
        let rowNodes = params.nodes;
        if (rowNodes.length === 0) {
            return;
        }
        let nextInsertPos = rowNodes.length - 1;
        //put total rows at the bottom after sorting
        for (let i = rowNodes.length - 1; i >= 0; i--) {
            if (rowNodes[i].data && rowNodes[i].data.total) {
                rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
                nextInsertPos--;
            }
        }
    }

    const getToolBar = () => (
        <div className="export-toolbar genBtn-blk">
            <button type="button" className="btn btn-default excel genBtn smallBtn"
                    onClick={onExportCSV}
            >Export CSV <FileDownloadIcon style={{fontSize:'18px'}} />
            </button>
            <button type="button" className="btn btn-default reset genBtn smallBtn"
                    onClick={onExportPDF}
            >Export PDF <FileDownloadIcon style={{fontSize:'18px'}}/>
            </button>
        </div>
    );

    return(
        <div>
            {!isLoading && graphData && graphData.length > 0 && getToolBar() }
            {graphData && graphData.length > 0 &&
                <div className="ag-grid-wrapper">
                    <div id={"classQuestionView"} className="ag-theme-balham"
                         style={{
                             height: (rowHeight * graphData.length + GridStyle.VERTICAL_HEADER_HEIGHT + GridStyle.GRID_PADDING_SML + GridStyle.HORIZONTAL_SCROLLBAR_PADDING - Math.abs(headerHeightOffset)) + 'px',
                             marginTop: Math.abs(headerHeightOffset)+'px'
                         }}>
                        <AgGridReact
                            ref={gridRef}
                            suppressPropertyNamesCheck={true}
                            suppressHorizontalScroll={true}
                            suppressRowTransform={true}
                            headerHeight={GridStyle.VERTICAL_HEADER_HEIGHT}
                            columnDefs={columnDefs}
                            rowData={graphData}
                            defaultColDef={defaultColDef}
                            rowHeight={rowHeight}
                            onGridReady={onGridReady}
                            onCellClicked={handleCellClick}
                            postSortRows={postSortRows}
                            components={{
                                passCellRenderer: PassCellRenderer,
                                failCellRenderer: FailCellRenderer
                            }}
                        />
                    </div>
                </div>
            }
            <TestQuestionResultDialog data={testQuestionDialogData} setData={setTestQuestionDialogData} />
        </div>
    )
}

export default ClassQuestionViewDiagnostic;
